import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, Trash2, Users2, AlertTriangle, ArrowUpRight, Send, X } from "lucide-react";

export default function Team() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [f, setF] = useState({ name: "", email: "", password: "", role: "member" });
  const [resendFor, setResendFor] = useState(null);
  const load = () => api.get("/company/members").then(({ data }) => setMembers(data));
  useEffect(() => { load(); }, []);

  const max = user?.max_users || 1;
  const count = members.length;
  const remaining = max - count;
  const atLimit = remaining <= 0;
  const nearLimit = remaining === 1;

  const add = async () => {
    if (!f.email || !f.password) { toast.error("Email e password richieste"); return; }
    try { await api.post("/company/members", f); toast.success("Membro aggiunto (invito inviato)"); setF({ name: "", email: "", password: "", role: "member" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/company/members/${id}`); load(); };
  const changeRole = async (id, role) => {
    try { await api.post(`/company/members/${id}/role`, { role }); toast.success("Ruolo aggiornato"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const inp = "border border-stone-300 bg-white px-3 py-2 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40";
  return (
    <div className="space-y-8">
      <div><p className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">{user?.company_name}</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-light text-stone-900">Il mio team</h1>
        <p className="text-sm text-stone-500 mt-2">Invita membri della tua azienda e assegna il ruolo.</p></div>

      {(nearLimit || atLimit) && (
        <div data-testid="team-limit-banner" className={`rounded-sm p-4 flex items-start gap-3 border ${atLimit ? "bg-alert/10 border-alert/30" : "bg-brand-accent/10 border-brand-accent/30"}`}>
          <AlertTriangle size={18} className={`mt-0.5 shrink-0 ${atLimit ? "text-alert" : "text-brand-accent"}`} />
          <div className="text-sm text-stone-700 flex-1">
            {atLimit
              ? <><b className="text-alert">Hai raggiunto il limite del tuo piano ({count}/{max} utenti).</b> Passa a un piano superiore per aggiungere altri membri.</>
              : <><b>Ti resta 1 solo posto</b> ({count}/{max} utenti). Valuta l'upgrade del piano per far crescere il team.</>}
          </div>
          <button data-testid="team-upgrade-btn" onClick={() => navigate("/billing")}
            className="shrink-0 flex items-center gap-1 rounded-full bg-brand text-white px-4 py-2 text-xs font-semibold hover:bg-brand-hover transition-colors">
            Aggiorna piano <ArrowUpRight size={14} />
          </button>
        </div>
      )}

      <div className="bg-white border border-stone-200 rounded-sm p-6">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
          <div className="flex items-center gap-2"><UserPlus size={18} className="text-brand" /><h3 className="font-serif text-2xl text-stone-900">Invita membro</h3></div>
          <span data-testid="team-usage" className={`text-xs font-bold px-2 py-1 rounded-full ${atLimit ? "text-alert bg-alert/10" : "text-brand bg-brand/10"}`}>{count}/{max} utenti</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input data-testid="member-name" placeholder="Nome" className={inp} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <input data-testid="member-email" placeholder="Email" className={inp} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
          <input data-testid="member-password" placeholder="Password iniziale" className={inp} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
          <select data-testid="member-role" className={inp} value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}><option value="member">Membro</option><option value="owner">Owner</option></select>
        </div>
        <button data-testid="member-add-btn" onClick={add} disabled={atLimit}
          className="mt-4 rounded-full bg-brand text-white px-6 py-2.5 text-sm font-semibold hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed">
          {atLimit ? "Limite raggiunto" : "Aggiungi"}
        </button>
      </div>

      <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
        <div className="p-6 border-b border-stone-200 flex items-center gap-2"><Users2 size={18} className="text-brand" /><h3 className="font-serif text-2xl text-stone-900">Membri</h3></div>
        <table className="w-full text-sm"><tbody>
          {members.map((m) => (
            <tr key={m.id} data-testid={`member-row-${m.id}`} className="border-b border-stone-100">
              <td className="py-4 px-6 font-semibold text-stone-800">{m.name}</td>
              <td className="py-4 px-6 text-stone-600">{m.email}</td>
              <td className="py-4 px-6">
                {m.id === user?.real_id ? (
                  <span className="text-xs font-bold text-brand bg-brand/10 px-2 py-1 rounded-full">{m.role}</span>
                ) : (
                  <select data-testid={`member-role-select-${m.id}`} value={m.role} onChange={(e) => changeRole(m.id, e.target.value)}
                    className="border border-stone-300 bg-white px-2 py-1 text-xs rounded-sm outline-none focus:ring-2 focus:ring-brand/40">
                    <option value="member">member</option>
                    <option value="owner">owner</option>
                  </select>
                )}
              </td>
              <td className="py-4 px-6 text-right">
                <div className="flex items-center justify-end gap-3">
                  {m.id !== user?.real_id && (
                    <button data-testid={`member-resend-${m.id}`} onClick={() => setResendFor(m)} className="text-stone-400 hover:text-brand flex items-center gap-1 text-xs" title="Reinvia invito / reimposta password"><Send size={15} /> Reinvia</button>
                  )}
                  {m.id !== user?.real_id && m.role !== "owner" && (
                    <button data-testid={`member-del-${m.id}`} onClick={() => del(m.id)} className="text-stone-400 hover:text-alert"><Trash2 size={16} /></button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody></table>
      </div>

      {resendFor && <ResendDialog member={resendFor} onClose={() => setResendFor(null)} />}
    </div>
  );
}

function ResendDialog({ member, onClose }) {
  const [password, setPassword] = useState("");
  const [sending, setSending] = useState(false);
  const submit = async () => {
    if (password && password.length < 6) { toast.error("La password deve avere almeno 6 caratteri"); return; }
    setSending(true);
    try {
      await api.post(`/company/members/${member.id}/resend`, password ? { password } : {});
      toast.success(password ? "Password reimpostata e invito reinviato" : "Invito reinviato");
      onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };
  return (
    <div className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div data-testid="resend-dialog" className="bg-white rounded-sm w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-stone-200">
          <h3 className="font-serif text-2xl text-stone-900">Reinvia invito</h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-800"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-stone-600">Reinvia l'email di invito a <b>{member.email}</b>.</p>
          <div>
            <label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Nuova password (opzionale)</label>
            <input data-testid="resend-password-input" type="text" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Lascia vuoto per non cambiarla"
              className="mt-2 w-full border border-stone-300 bg-white px-4 py-3 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40" />
            <p className="text-xs text-stone-400 mt-1">Se inserita, la password del membro verrà reimpostata e inclusa nell'email.</p>
          </div>
        </div>
        <div className="p-6 border-t border-stone-200 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2.5 text-sm text-stone-600 hover:bg-stone-100 rounded-none">Annulla</button>
          <button data-testid="resend-submit-btn" onClick={submit} disabled={sending}
            className="rounded-full bg-brand text-white px-6 py-2.5 text-sm font-semibold hover:bg-brand-hover disabled:opacity-60">
            {sending ? "Invio…" : "Reinvia invito"}
          </button>
        </div>
      </div>
    </div>
  );
}
